import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  VStack,
  HStack,
  Grid,
  GridItem,
  Text,
  Switch,
  NumberInput,
  NumberInputField,
  Alert,
  AlertIcon,
  Divider,
  Box,
  Code,
  Collapse,
  IconButton,
} from "@chakra-ui/react";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import { toast } from "react-hot-toast";
import { manhourRateAPI } from "../../services/api";

const ManhourRateFormModal = ({ isOpen, onClose, rate, onSuccess }) => {
  // Form state
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    rateType: "global",
    skillLevel: "",
    currency: "USD",
    hourlyRate: "",
    overtimeRate: "",
    holidayRate: "",
    effectiveFrom: "",
    effectiveTo: "",
    masterCategoryId: "",
    masterActivityId: "",
    priority: 0,
    isActive: true,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [detailedError, setDetailedError] = useState(null);
  const [showErrorDetails, setShowErrorDetails] = useState(false);
  const [masterCategories, setMasterCategories] = useState([]);
  const [masterActivities, setMasterActivities] = useState([]);

  const isEditing = Boolean(rate);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      if (rate) {
        // Editing mode - populate form with rate data
        setFormData({
          name: rate.name || "",
          description: rate.description || "",
          rateType: rate.rateType || "global",
          skillLevel: rate.skillLevel || "",
          currency: rate.currency || "USD",
          hourlyRate: rate.hourlyRate?.toString() || "",
          overtimeRate: rate.overtimeRate?.toString() || "",
          holidayRate: rate.holidayRate?.toString() || "",
          effectiveFrom: rate.effectiveFrom 
            ? new Date(rate.effectiveFrom).toISOString().split('T')[0] 
            : "",
          effectiveTo: rate.effectiveTo 
            ? new Date(rate.effectiveTo).toISOString().split('T')[0] 
            : "",
          masterCategoryId: rate.masterCategoryId || "",
          masterActivityId: rate.masterActivityId || "",
          priority: rate.priority || 0,
          isActive: rate.isActive !== undefined ? rate.isActive : true,
        });
      } else {
        // Create mode - reset to defaults
        setFormData({
          name: "",
          description: "",
          rateType: "global",
          skillLevel: "",
          currency: "USD",
          hourlyRate: "",
          overtimeRate: "",
          holidayRate: "",
          effectiveFrom: new Date().toISOString().split('T')[0],
          effectiveTo: "",
          masterCategoryId: "",
          masterActivityId: "",
          priority: 0,
          isActive: true,
        });
      }
      setError(null);
      setDetailedError(null);
      setShowErrorDetails(false);
      loadMasterCategories();
    }
  }, [isOpen, rate]);

  // Load master categories when category or activity rate type is selected
  const loadMasterCategories = async () => {
    try {
      const response = await manhourRateAPI.getMasterCategories();
      console.log("Master categories response:", response);
      setMasterCategories(response.data?.items || []);
    } catch (error) {
      console.error("Failed to load master categories:", error);
      // Don't show error for missing master categories - it's expected if not set up
      setMasterCategories([]);
    }
  };

  // Load master activities when category is selected
  const loadMasterActivities = async (categoryId) => {
    if (!categoryId) {
      setMasterActivities([]);
      return;
    }

    try {
      const response = await manhourRateAPI.getMasterActivities(categoryId);
      console.log("Master activities response:", response);
      setMasterActivities(response.data?.items || []);
    } catch (error) {
      console.error("Failed to load master activities:", error);
      setMasterActivities([]);
    }
  };

  // Handle form field changes
  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Special handling for rate type changes
    if (field === "rateType") {
      setFormData((prev) => ({
        ...prev,
        skillLevel: "",
        masterCategoryId: "",
        masterActivityId: "",
      }));
      setMasterActivities([]);
    }

    // Load activities when category changes
    if (field === "masterCategoryId") {
      setFormData((prev) => ({
        ...prev,
        masterActivityId: "",
      }));
      loadMasterActivities(value);
    }
  };

  // Form validation
  const validateForm = () => {
    const errors = [];

    if (!formData.name.trim()) errors.push("Rate name is required");
    if (!formData.hourlyRate) errors.push("Hourly rate is required");
    if (!formData.currency) errors.push("Currency is required");
    if (!formData.effectiveFrom) errors.push("Effective from date is required");

    // Rate type specific validation
    if (formData.rateType === "category" && !formData.masterCategoryId) {
      errors.push("Master Category is required for category-specific rates");
    }
    if (formData.rateType === "activity" && !formData.masterActivityId) {
      errors.push("Master Activity is required for activity-specific rates");
    }
    if (formData.rateType === "skill_level" && !formData.skillLevel) {
      errors.push("Skill level is required for skill-level rates");
    }

    // Date validation
    if (formData.effectiveTo && formData.effectiveFrom >= formData.effectiveTo) {
      errors.push("Effective To date must be after Effective From date");
    }

    // Numeric validation
    if (parseFloat(formData.hourlyRate) <= 0) {
      errors.push("Hourly rate must be greater than 0");
    }
    if (formData.overtimeRate && parseFloat(formData.overtimeRate) <= 0) {
      errors.push("Overtime rate must be greater than 0");
    }
    if (formData.holidayRate && parseFloat(formData.holidayRate) <= 0) {
      errors.push("Holiday rate must be greater than 0");
    }

    return errors;
  };

  // ✅ NEW: Test mode - simulate successful submission
  const handleTestSubmit = async () => {
    setLoading(true);
    setError(null);
    setDetailedError(null);

    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    console.log("✅ TEST: Would create rate with data:", {
      ...formData,
      hourlyRate: parseFloat(formData.hourlyRate),
      overtimeRate: formData.overtimeRate ? parseFloat(formData.overtimeRate) : null,
      holidayRate: formData.holidayRate ? parseFloat(formData.holidayRate) : null,
    });

    setLoading(false);
    toast.success(`Manhour rate ${isEditing ? 'updated' : 'created'} successfully (TEST MODE)`);
    onSuccess();
  };

  // Handle form submission
  // Handle form submission
const handleSubmit = async (e) => {
  e.preventDefault();
  
  const validationErrors = validateForm();
  if (validationErrors.length > 0) {
    setError(validationErrors.join(", "));
    return;
  }

  setLoading(true);
  setError(null);
  setDetailedError(null);

  try {
    // ✅ FIX: Prepare data with proper null handling for backend validation
    const apiData = {
      name: formData.name.trim(),
      rateType: formData.rateType,
      currency: formData.currency,
      hourlyRate: parseFloat(formData.hourlyRate),
      effectiveFrom: formData.effectiveFrom,
      priority: parseInt(formData.priority) || 0,
      isActive: formData.isActive,
    };

    // ✅ Only include optional fields if they have actual values
    if (formData.description && formData.description.trim()) {
      apiData.description = formData.description.trim();
    }

    if (formData.skillLevel && formData.skillLevel.trim()) {
      apiData.skillLevel = formData.skillLevel;
    }

    if (formData.overtimeRate && formData.overtimeRate.toString().trim()) {
      apiData.overtimeRate = parseFloat(formData.overtimeRate);
    }

    if (formData.holidayRate && formData.holidayRate.toString().trim()) {
      apiData.holidayRate = parseFloat(formData.holidayRate);
    }

    if (formData.effectiveTo && formData.effectiveTo.trim()) {
      apiData.effectiveTo = formData.effectiveTo;
    }

    if (formData.masterCategoryId && formData.masterCategoryId.trim()) {
      apiData.masterCategoryId = formData.masterCategoryId;
    }

    if (formData.masterActivityId && formData.masterActivityId.trim()) {
      apiData.masterActivityId = formData.masterActivityId;
    }

    console.log("🚀 Submitting API data (cleaned):", apiData);

    if (isEditing) {
      await manhourRateAPI.updateManhourRate(rate.id, apiData);
      toast.success("Manhour rate updated successfully");
    } else {
      await manhourRateAPI.createManhourRate(apiData);
      toast.success("Manhour rate created successfully");
    }

    onSuccess();
  } catch (error) {
    console.error("Form submission error:", error);
    
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        "Failed to save manhour rate";

    setError(errorMessage);
    
    setDetailedError({
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      config: {
        method: error.config?.method,
        url: error.config?.url,
        data: error.config?.data,
      }
    });

    if (error.response?.status === 400 || error.response?.status === 404) {
      if (error.response?.data?.message?.includes('validation') ||
          error.response?.data?.message?.includes('required') ||
          error.response?.data?.message?.includes('invalid')) {
        setError(`Validation Error: ${error.response.data.message}`);
      } else {
        setError("The manhour rates API may not be properly configured. Please check the backend setup.");
      }
    }
  } finally {
    setLoading(false);
  }
};


  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      size="4xl" 
      scrollBehavior="inside"
      closeOnOverlayClick={false}
    >
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent
        bg="linear-gradient(145deg, #1e2936 0%, #1a202c 100%)"
        border="1px solid"
        borderColor="whiteAlpha.200"
        color="white"
        maxH="90vh"
        mx={4}
      >
        <ModalHeader 
          pb={4}
          position="sticky"
          top={0}
          bg="linear-gradient(145deg, #1e2936 0%, #1a202c 100%)"
          zIndex={10}
          borderBottom="1px solid"
          borderColor="whiteAlpha.200"
        >
          <Text fontSize="2xl" fontWeight="bold">
            {isEditing ? "Edit Manhour Rate" : "Create New Manhour Rate"}
          </Text>
        </ModalHeader>
        <ModalCloseButton 
          position="sticky"
          top={4}
          right={4}
          zIndex={11}
        />

        <form onSubmit={handleSubmit}>
          <ModalBody 
            pb={6}
            overflowY="auto"
            maxH="calc(90vh - 140px)"
            css={{
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(255, 255, 255, 0.3)',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: 'rgba(255, 255, 255, 0.5)',
              },
            }}
          >
            {error && (
              <Alert status="error" mb={4} bg="red.900" color="red.100">
                <AlertIcon />
                <VStack align="start" spacing={2} flex={1}>
                  <Text>{error}</Text>
                  {detailedError && (
                    <>
                      <HStack>
                        <Text fontSize="sm">
                          Status: {detailedError.status} - {detailedError.statusText}
                        </Text>
                        <IconButton
                          size="xs"
                          icon={showErrorDetails ? <FiChevronUp /> : <FiChevronDown />}
                          onClick={() => setShowErrorDetails(!showErrorDetails)}
                          variant="ghost"
                          aria-label="Toggle error details"
                        />
                      </HStack>
                      <Collapse in={showErrorDetails} animateOpacity>
                        <Box p={2} bg="blackAlpha.300" rounded="md" fontSize="xs">
                          <Text fontWeight="bold" mb={2}>API Error Details:</Text>
                          <Code colorScheme="red" p={2} display="block" whiteSpace="pre-wrap">
                            {JSON.stringify(detailedError.data, null, 2)}
                          </Code>
                        </Box>
                      </Collapse>
                    </>
                  )}
                </VStack>
              </Alert>
            )}

            <VStack spacing={6} align="stretch">
              {/* Basic Information */}
              <Box>
                <Text fontSize="lg" fontWeight="semibold" mb={3} color="cyan.400">
                  Basic Information
                </Text>
                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                  <GridItem colSpan={2}>
                    <FormControl isRequired>
                      <FormLabel>Rate Name</FormLabel>
                      <Input
                        value={formData.name}
                        onChange={(e) => handleChange("name", e.target.value)}
                        placeholder="e.g., Senior Engineer Hourly Rate"
                        bg="whiteAlpha.100"
                        border="1px solid"
                        borderColor="whiteAlpha.300"
                        _focus={{
                          borderColor: "blue.500",
                          boxShadow: "0 0 0 1px #3182ce",
                        }}
                      />
                    </FormControl>
                  </GridItem>

                  <GridItem colSpan={2}>
                    <FormControl>
                      <FormLabel>Description</FormLabel>
                      <Textarea
                        value={formData.description}
                        onChange={(e) => handleChange("description", e.target.value)}
                        placeholder="Optional description of this rate"
                        bg="whiteAlpha.100"
                        border="1px solid"
                        borderColor="whiteAlpha.300"
                        rows={3}
                        resize="vertical"
                        _focus={{
                          borderColor: "blue.500",
                          boxShadow: "0 0 0 1px #3182ce",
                        }}
                      />
                    </FormControl>
                  </GridItem>
                </Grid>
              </Box>

              <Divider />

              {/* Rate Type Configuration */}
              <Box>
                <Text fontSize="lg" fontWeight="semibold" mb={3} color="cyan.400">
                  Rate Type & Scope
                </Text>
                <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4}>
                  <GridItem>
                    <FormControl isRequired>
                      <FormLabel>Rate Type</FormLabel>
                      <Select
                        value={formData.rateType}
                        onChange={(e) => handleChange("rateType", e.target.value)}
                        bg="whiteAlpha.100"
                        border="1px solid"
                        borderColor="whiteAlpha.300"
                        _focus={{
                          borderColor: "blue.500",
                          boxShadow: "0 0 0 1px #3182ce",
                        }}
                      >
                        <option value="global">Global Rate</option>
                        <option value="category">Category-Specific</option>
                        <option value="activity">Activity-Specific</option>
                        <option value="skill_level">Skill Level</option>
                      </Select>
                    </FormControl>
                  </GridItem>

                  <GridItem>
                    <FormControl>
                      <FormLabel>Priority</FormLabel>
                      <NumberInput
                        value={formData.priority}
                        onChange={(value) => handleChange("priority", value)}
                        min={0}
                      >
                        <NumberInputField
                          bg="whiteAlpha.100"
                          border="1px solid"
                          borderColor="whiteAlpha.300"
                          _focus={{
                            borderColor: "blue.500",
                            boxShadow: "0 0 0 1px #3182ce",
                          }}
                        />
                      </NumberInput>
                      <Text fontSize="xs" color="gray.400" mt={1}>
                        Higher priority rates take precedence
                      </Text>
                    </FormControl>
                  </GridItem>

                  {/* Conditional fields based on rate type */}
                  {formData.rateType === "skill_level" && (
                    <GridItem colSpan={{ base: 1, md: 2 }}>
                      <FormControl isRequired>
                        <FormLabel>Skill Level</FormLabel>
                        <Select
                          value={formData.skillLevel}
                          onChange={(e) => handleChange("skillLevel", e.target.value)}
                          placeholder="Select skill level"
                          bg="whiteAlpha.100"
                          border="1px solid"
                          borderColor="whiteAlpha.300"
                          _focus={{
                            borderColor: "blue.500",
                            boxShadow: "0 0 0 1px #3182ce",
                          }}
                        >
                          <option value="helper">Helper</option>
                          <option value="skilled">Skilled Worker</option>
                          <option value="supervisor">Supervisor</option>
                          <option value="specialist">Specialist</option>
                          <option value="engineer">Engineer</option>
                        </Select>
                      </FormControl>
                    </GridItem>
                  )}

                  {(formData.rateType === "category" || formData.rateType === "activity") && (
                    <GridItem>
                      <FormControl isRequired={formData.rateType === "category"}>
                        <FormLabel>Master Category</FormLabel>
                        <Select
                          value={formData.masterCategoryId}
                          onChange={(e) => handleChange("masterCategoryId", e.target.value)}
                          placeholder="Select category"
                          bg="whiteAlpha.100"
                          border="1px solid"
                          borderColor="whiteAlpha.300"
                          _focus={{
                            borderColor: "blue.500",
                            boxShadow: "0 0 0 1px #3182ce",
                          }}
                        >
                          {masterCategories.map((category) => (
                            <option key={category.id} value={category.id}>
                              {category.name} ({category.code})
                            </option>
                          ))}
                        </Select>
                        {masterCategories.length === 0 && (
                          <Text fontSize="xs" color="yellow.400" mt={1}>
                            No categories available - may need backend setup
                          </Text>
                        )}
                      </FormControl>
                    </GridItem>
                  )}

                  {formData.rateType === "activity" && (
                    <GridItem>
                      <FormControl isRequired>
                        <FormLabel>Master Activity</FormLabel>
                        <Select
                          value={formData.masterActivityId}
                          onChange={(e) => handleChange("masterActivityId", e.target.value)}
                          placeholder="Select activity"
                          bg="whiteAlpha.100"
                          border="1px solid"
                          borderColor="whiteAlpha.300"
                          isDisabled={!formData.masterCategoryId}
                          _focus={{
                            borderColor: "blue.500",
                            boxShadow: "0 0 0 1px #3182ce",
                          }}
                        >
                          {masterActivities.map((activity) => (
                            <option key={activity.id} value={activity.id}>
                              {activity.name}
                            </option>
                          ))}
                        </Select>
                        {formData.rateType === "activity" && !formData.masterCategoryId && (
                          <Text fontSize="xs" color="gray.400" mt={1}>
                            Select a category first
                          </Text>
                        )}
                      </FormControl>
                    </GridItem>
                  )}
                </Grid>
              </Box>

              <Divider />

              {/* Rate Information */}
              <Box>
                <Text fontSize="lg" fontWeight="semibold" mb={3} color="cyan.400">
                  Rate Information
                </Text>
                <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4}>
                  <GridItem>
                    <FormControl isRequired>
                      <FormLabel>Currency</FormLabel>
                      <Select
                        value={formData.currency}
                        onChange={(e) => handleChange("currency", e.target.value)}
                        bg="whiteAlpha.100"
                        border="1px solid"
                        borderColor="whiteAlpha.300"
                        _focus={{
                          borderColor: "blue.500",
                          boxShadow: "0 0 0 1px #3182ce",
                        }}
                      >
                        <option value="USD">USD - US Dollar</option>
                        <option value="AED">AED - UAE Dirham</option>
                        <option value="EUR">EUR - Euro</option>
                        <option value="GBP">GBP - British Pound</option>
                        <option value="SAR">SAR - Saudi Riyal</option>
                        <option value="INR">INR - Indian Rupee</option>
                      </Select>
                    </FormControl>
                  </GridItem>

                  <GridItem>
                    <FormControl isRequired>
                      <FormLabel>Hourly Rate</FormLabel>
                      <NumberInput
                        value={formData.hourlyRate}
                        onChange={(value) => handleChange("hourlyRate", value)}
                        min={0}
                        precision={2}
                      >
                        <NumberInputField
                          bg="whiteAlpha.100"
                          border="1px solid"
                          borderColor="whiteAlpha.300"
                          _focus={{
                            borderColor: "blue.500",
                            boxShadow: "0 0 0 1px #3182ce",
                          }}
                        />
                      </NumberInput>
                    </FormControl>
                  </GridItem>

                  <GridItem>
                    <FormControl>
                      <FormLabel>Overtime Rate (Optional)</FormLabel>
                      <NumberInput
                        value={formData.overtimeRate}
                        onChange={(value) => handleChange("overtimeRate", value)}
                        min={0}
                        precision={2}
                      >
                        <NumberInputField
                          bg="whiteAlpha.100"
                          border="1px solid"
                          borderColor="whiteAlpha.300"
                          _focus={{
                            borderColor: "blue.500",
                            boxShadow: "0 0 0 1px #3182ce",
                          }}
                        />
                      </NumberInput>
                    </FormControl>
                  </GridItem>

                  <GridItem>
                    <FormControl>
                      <FormLabel>Holiday Rate (Optional)</FormLabel>
                      <NumberInput
                        value={formData.holidayRate}
                        onChange={(value) => handleChange("holidayRate", value)}
                        min={0}
                        precision={2}
                      >
                        <NumberInputField
                          bg="whiteAlpha.100"
                          border="1px solid"
                          borderColor="whiteAlpha.300"
                          _focus={{
                            borderColor: "blue.500",
                            boxShadow: "0 0 0 1px #3182ce",
                          }}
                        />
                      </NumberInput>
                    </FormControl>
                  </GridItem>
                </Grid>
              </Box>

              <Divider />

              {/* Effective Period */}
              <Box>
                <Text fontSize="lg" fontWeight="semibold" mb={3} color="cyan.400">
                  Effective Period
                </Text>
                <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4}>
                  <GridItem>
                    <FormControl isRequired>
                      <FormLabel>Effective From</FormLabel>
                      <Input
                        type="date"
                        value={formData.effectiveFrom}
                        onChange={(e) => handleChange("effectiveFrom", e.target.value)}
                        bg="whiteAlpha.100"
                        border="1px solid"
                        borderColor="whiteAlpha.300"
                        _focus={{
                          borderColor: "blue.500",
                          boxShadow: "0 0 0 1px #3182ce",
                        }}
                      />
                    </FormControl>
                  </GridItem>

                  <GridItem>
                    <FormControl>
                      <FormLabel>Effective To (Optional)</FormLabel>
                      <Input
                        type="date"
                        value={formData.effectiveTo}
                        onChange={(e) => handleChange("effectiveTo", e.target.value)}
                        bg="whiteAlpha.100"
                        border="1px solid"
                        borderColor="whiteAlpha.300"
                        _focus={{
                          borderColor: "blue.500",
                          boxShadow: "0 0 0 1px #3182ce",
                        }}
                      />
                      <Text fontSize="xs" color="gray.400" mt={1}>
                        Leave empty for indefinite period
                      </Text>
                    </FormControl>
                  </GridItem>
                </Grid>
              </Box>

              <Divider />

              {/* Status */}
              <Box>
                <FormControl display="flex" alignItems="center">
                  <FormLabel htmlFor="isActive" mb="0">
                    Active Status
                  </FormLabel>
                  <Switch
                    id="isActive"
                    isChecked={formData.isActive}
                    onChange={(e) => handleChange("isActive", e.target.checked)}
                    colorScheme="green"
                  />
                  <Text ml={3} fontSize="sm" color="gray.400">
                    {formData.isActive ? "Active" : "Inactive"}
                  </Text>
                </FormControl>
              </Box>

              <Box pb={4} />
            </VStack>
          </ModalBody>

          <ModalFooter 
            position="sticky"
            bottom={0}
            bg="linear-gradient(145deg, #1e2936 0%, #1a202c 100%)"
            borderTop="1px solid"
            borderColor="whiteAlpha.200"
            zIndex={10}
            pt={4}
          >
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            
            {/* ✅ NEW: Test Mode Button for debugging */}
            {/* <Button
              colorScheme="yellow"
              variant="outline"
              mr={3}
              onClick={handleTestSubmit}
              isLoading={loading}
              loadingText="Testing..."
            >
              Test Form
            </Button> */}

            <Button
              type="submit"
              colorScheme="blue"
              isLoading={loading}
              loadingText={isEditing ? "Updating..." : "Creating..."}
            >
              {isEditing ? "Update Rate" : "Create Rate"}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default ManhourRateFormModal;
